/**
 * Cálculos de métricas para contratos de leasing y préstamos.
 * "Pagada" = cuota con due_date < fecha de corte (hoy por defecto).
 */

export interface ScheduleInstallment {
  installment_number: number;
  due_date: string; // ISO date
  principal: number; // recuperación coste (leasing) o amortización capital (préstamo)
  interest: number; // carga financiera / intereses
  insurance: number;
  total: number;
  pending_capital?: number | null; // saldo de capital tras esta cuota (si viene de la fuente)
}


export interface ContractMetrics {
  totalInstallments: number;
  paidInstallments: number;
  pendingInstallments: number;
  initialCapital: number;
  pendingCapital: number;
  pendingInterest: number;
  totalPending: number;
  averageInstallment: number;
  nextInstallment: ScheduleInstallment | null;
  status: "vigente" | "finalizado" | "por_vencer";
  /** El cuadro termina sin saldar (última fila con capital pendiente > 0). */
  scheduleIncomplete: boolean;
  /** Estamos a <= 1 mes de la última cuota registrada y el cuadro no salda: hay que pedir el resto. */
  needsMoreSchedule: boolean;
  /** Fecha (ISO) de la última cuota registrada, útil para tooltips. */
  lastScheduledDate: string | null;
}


export function computeContractMetrics(
  schedule: ScheduleInstallment[],
  cutoffDate: Date = new Date(),
  pendingCapitalOverride?: number | null,
): ContractMetrics {
  const cutoff = new Date(cutoffDate);
  cutoff.setHours(0, 0, 0, 0);
  const cutoffISO = cutoff.toISOString().slice(0, 10);

  const sorted = [...schedule].sort((a, b) => a.due_date.localeCompare(b.due_date));
  const paid = sorted.filter((s) => s.due_date < cutoffISO);
  const pending = sorted.filter((s) => s.due_date >= cutoffISO);

  const initialCapital = sorted.reduce((sum, s) => sum + (Number(s.principal) || 0), 0);
  const computedPendingCap = pending.reduce((sum, s) => sum + (Number(s.principal) || 0), 0);
  const pendingCapital = (pendingCapitalOverride != null && !Number.isNaN(Number(pendingCapitalOverride)))
    ? Number(pendingCapitalOverride)
    : computedPendingCap;
  const pendingInterest = pending.reduce((sum, s) => sum + (Number(s.interest) || 0), 0);
  const totalPending = pending.reduce((sum, s) => sum + (Number(s.total) || 0), 0);
  const totalSum = sorted.reduce((sum, s) => sum + (Number(s.total) || 0), 0);

  const next = pending[0] || null;

  let status: ContractMetrics["status"] = "vigente";
  if (pending.length === 0) status = "finalizado";
  else if (next) {
    const sixMonths = new Date(cutoff);
    sixMonths.setMonth(sixMonths.getMonth() + 6);
    const lastDate = new Date(sorted[sorted.length - 1].due_date);
    if (lastDate <= sixMonths) status = "por_vencer";
  }

  // Detectar cuadros de amortización incompletos: última fila con capital pendiente > 0.
  const lastRow = sorted[sorted.length - 1] || null;
  const lastPending = lastRow && lastRow.pending_capital != null ? Number(lastRow.pending_capital) : null;
  const scheduleIncomplete = lastPending != null && lastPending > 0.01;

  let needsMoreSchedule = false;
  if (scheduleIncomplete && lastRow) {
    const oneMonthBefore = new Date(lastRow.due_date);
    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
    if (cutoff >= oneMonthBefore) needsMoreSchedule = true;
  }

  return {
    totalInstallments: sorted.length,
    paidInstallments: paid.length,
    pendingInstallments: pending.length,
    initialCapital,
    pendingCapital,
    pendingInterest,
    totalPending,
    averageInstallment: sorted.length ? totalSum / sorted.length : 0,
    nextInstallment: next,
    status,
    scheduleIncomplete,
    needsMoreSchedule,
    lastScheduledDate: lastRow ? lastRow.due_date : null,
  };
}


